import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { NavLink } from '../NavLink';
import { Divider } from './Divider';
import { NVXLogo } from './NVXLogo';
import { TenantSwitcher } from './TenantSwitcher';
import { ContainerIcon } from '@/components/ui/custom-icons/ContainerIcon';
import { DashboardIcon } from '@/components/ui/custom-icons/DashboardIcon';
import { RoadTransportIcon } from '@/components/ui/custom-icons/RoadTransportIcon';
import { useAppSelector } from '@/store/hooks';
import { selectTenantModules, type TenantModule } from '@/store/slices/tenantSlice';
interface NavItem {
  href: string;
  icon: string | React.ReactNode;
  labelKey: string;
  requiredModule?: TenantModule | TenantModule[];
}

const mainNavItems: NavItem[] = [
  { href: '/dashboard', icon: <DashboardIcon />, labelKey: 'dashboard' },
  { href: '/road-transport', icon: <RoadTransportIcon />, labelKey: 'roadTransport', requiredModule: 'RoadTransport' },
  { href: '/inbound', icon: 'pi pi-sign-in', labelKey: 'inbound', requiredModule: 'Warehouse' },
  { href: '/outbound', icon: 'pi pi-sign-out', labelKey: 'outbound', requiredModule: 'Warehouse' },
  { href: '/stock', icon: 'pi pi-warehouse', labelKey: 'stock', requiredModule: 'Warehouse' },
  { href: '/address-book', icon: 'pi pi-address-book', labelKey: 'addressBook', requiredModule: ['DraftOutboundOrders', 'DraftRoadTransportOrders'] },
  { href: '/billing', icon: 'pi pi-wallet', labelKey: 'billing', requiredModule: 'Billing' },
  { href: '/containers', icon: <ContainerIcon size={24} />, labelKey: 'containers', requiredModule: 'Containers' },
];

export interface MainNavigationProps {
  expanded?: boolean;
}

export function MainNavigation({ expanded = false }: MainNavigationProps) {
  const { t } = useTranslation('navigation');
  const modules = useAppSelector(selectTenantModules);
  const visibleItems = useMemo(() => {
    return mainNavItems.filter((item) => {
      if (!item.requiredModule) return true;
      const required = Array.isArray(item.requiredModule) ? item.requiredModule : [item.requiredModule];
      return required.some((module) => modules.includes(module));
    });
  }, [modules]);

  return (
    <div className="flex flex-col items-start gap-(--sidebar-gap)">
      {/* Logo */}
      <div
        className={cn(
          'flex items-center justify-start w-full',
          'px-2.5 -translate-x-[9px]',
          'transition-all duration-200 ease-in-out'
        )}
      >
        <NVXLogo />
      </div>

      {/* Tenant Switcher */}
      <TenantSwitcher expanded={expanded} />
      <Divider />

      {/* Main Navigation */}
      <nav
        aria-label={t('mainNavigation')}
        className={cn(
          'flex flex-col gap-(--sidebar-gap)',
          'w-full items-stretch'
        )}
      >
        {visibleItems.map((item) => (
          <NavLink
            key={item.href}
            href={item.href}
            icon={item.icon}
            label={t(item.labelKey)}
            showLabel={expanded}
          />
        ))}
      </nav>
    </div>
  );
}
